import { QueryClient } from "@tanstack/react-query";
import { createAppKit } from "@reown/appkit/react";
import { base as appKitBase, baseSepolia as appKitBaseSepolia, mainnet as appKitMainnet, sepolia as appKitSepolia, type AppKitNetwork } from "@reown/appkit/networks";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { createConfig, http, injected, type Config } from "wagmi";
import { base as wagmiBase, baseSepolia as wagmiBaseSepolia, mainnet as wagmiMainnet, sepolia as wagmiSepolia } from "wagmi/chains";
import { EXPECTED_CHAIN_ID, envString } from "../utils/config";

export const reownProjectId = envString("VITE_REOWN_PROJECT_ID") ?? "";
export const isReownConfigured = reownProjectId.length > 0;
export const queryClient = new QueryClient();

const appKitNetwork = EXPECTED_CHAIN_ID === appKitBase.id ? appKitBase : EXPECTED_CHAIN_ID === appKitBaseSepolia.id ? appKitBaseSepolia : EXPECTED_CHAIN_ID === appKitSepolia.id ? appKitSepolia : appKitMainnet;
const wagmiChain = EXPECTED_CHAIN_ID === wagmiBase.id ? wagmiBase : EXPECTED_CHAIN_ID === wagmiBaseSepolia.id ? wagmiBaseSepolia : EXPECTED_CHAIN_ID === wagmiSepolia.id ? wagmiSepolia : wagmiMainnet;
const appKitNetworks = [appKitNetwork] as [AppKitNetwork, ...AppKitNetwork[]];
const wagmiChains = [wagmiChain] as const;
const metadata = {
  name: "CODE APE Genesis",
  description: "AI-agent CODE APE minting and launch utility.",
  url: "https://code-ape.com",
  icons: ["https://code-ape.com/icon.svg"]
};

function createFallbackWagmiConfig(): Config {
  return createConfig({
    chains: wagmiChains,
    connectors: [injected()],
    transports: {
      [wagmiMainnet.id]: http(),
      [wagmiBase.id]: http(),
      [wagmiBaseSepolia.id]: http(),
      [wagmiSepolia.id]: http()
    }
  });
}

function createReownWagmiConfig(): Config {
  const wagmiAdapter = new WagmiAdapter({
    networks: appKitNetworks,
    projectId: reownProjectId
  });

  const appKit = createAppKit({
    adapters: [wagmiAdapter],
    networks: appKitNetworks,
    defaultNetwork: appKitNetwork,
    allowUnsupportedChain: true,
    enableReconnect: false,
    projectId: reownProjectId,
    metadata,
    features: {
      analytics: true,
      email: false,
      socials: false,
      swaps: false,
      onramp: false
    },
    themeMode: "dark",
    themeVariables: {
      "--w3m-accent": "#00ff41"
    }
  });

  if (typeof window !== "undefined") {
    for (const delayMs of [0, 500, 1500]) {
      window.setTimeout(() => {
        void appKit.close().catch(() => undefined);
      }, delayMs);
    }
  }

  return wagmiAdapter.wagmiConfig;
}

function createSafeWagmiConfig(): Config {
  if (!isReownConfigured) return createFallbackWagmiConfig();
  try {
    return createReownWagmiConfig();
  } catch {
    return createFallbackWagmiConfig();
  }
}

export const wagmiConfig = createSafeWagmiConfig();
